/**
 * PostHog Analytics Integration
 * Story 355: Web analytics SDK integration
 *
 * Provides PostHog configuration and utility functions for analytics.
 * PostHogProvider handles initialization; these functions provide typed access.
 */
import posthog from 'posthog-js';

/**
 * PostHog configuration for the provider
 *
 * Export this to main.tsx for PostHogProvider initialization.
 */
export const posthogConfig = {
  apiKey: import.meta.env.VITE_PUBLIC_POSTHOG_KEY || '',
  options: {
    // FALLBACK: EU host is correct for our PostHog project; only used when apiKey is set
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    capture_pageview: false, // We handle pageviews manually for SPA
    capture_pageleave: true,
    persistence: 'localStorage' as const,
    autocapture: true,
    session_recording: {
      maskAllInputs: false,
    },
  },
};

// Log warning if not configured (dev convenience)
if (!posthogConfig.apiKey && typeof window !== 'undefined') {
  console.warn('[PostHog] VITE_PUBLIC_POSTHOG_KEY not configured, analytics disabled');
}

/**
 * Identify the current user
 *
 * Call this after successful authentication.
 * Links anonymous events to the user profile.
 */
export function identifyUser(userId: string, email?: string): void {
  if (!posthogConfig.apiKey) return;

  const properties: Record<string, string> = {};
  if (email) {
    properties.email = email;
  }

  posthog.identify(userId, properties);
}

/**
 * Reset user identity
 *
 * Call this on logout to ensure subsequent events
 * are not attributed to the logged-out user.
 */
export function resetUser(): void {
  if (!posthogConfig.apiKey) return;
  posthog.reset();
}

/**
 * Track a page view
 *
 * Call this on route changes in SPA.
 */
export function trackPageView(path?: string): void {
  if (!posthogConfig.apiKey) return;
  posthog.capture('$pageview', path ? { $current_url: path } : undefined);
}

/**
 * Track a custom event
 *
 * @param eventName - Name of the event
 * @param properties - Optional event properties
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!posthogConfig.apiKey) return;
  posthog.capture(eventName, properties);
}
